import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { MiTab } from "@miplatform/components";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import {
  CustomerPricingDetailsTabs,
  IAssignPriceLevelPayload,
} from "../../CustomerPricingDetails/CustomerPricingDetails.types";
import {
  useCustomerPricingDetailsContext,
  useTabFormRegistration,
} from "../../CustomerPricingDetails/CustomerPricingDetailsProvider";
import { PriceLevelHistory } from "../../components/PriceLevelHistory";
import { PriceLevelSelection } from "../../components/PriceLevelSelection";
import { customerPriceLevelTabSchema } from "../../schema/customerPricingDetails.schema";
import { useCustomerPriceLevelTabViewModel } from "./useCustomerPriceLevelTabViewModel";

const internalTabs = [
  {
    label: "Price Matrix",
    value: CustomerPricingDetailsTabs.PriceMatrix,
  },
  {
    label: "History",
    value: CustomerPricingDetailsTabs.History,
  },
];

export const CustomerPriceLevelTab = () => {
  const { accountDetails, setSaveHandler } = useCustomerPricingDetailsContext();

  const [activeTab, setActiveTab] = useState<string | number | undefined>(
    CustomerPricingDetailsTabs.PriceMatrix
  );

  const form = useForm<IAssignPriceLevelPayload>({
    mode: "onChange",
    defaultValues: {
      accountId: "",
      priceLevelId: "",
    },
    resolver: zodResolver(customerPriceLevelTabSchema),
  });
  const { control, reset, getValues } = form;

  const {
    priceLevels,
    categories,
    categoryDrops,
    priceLevelActivities,
    getPriceLevels,
    getCategories,
    getCategoryDrops,
    createAccountPriceLevel,
    getPriceLevelActivities,
    isPriceMatrixLoading,
    isHistoryLoading,
  } = useCustomerPriceLevelTabViewModel();

  const isLoading = isPriceMatrixLoading || isHistoryLoading;

  useTabFormRegistration(form, isLoading);

  useEffect(() => {
    getPriceLevels();
    getCategories();
    getCategoryDrops();
  }, []);

  // set form data
  useEffect(() => {
    if (accountDetails) {
      reset({
        accountId: accountDetails.id,
        priceLevelId: accountDetails.priceLevelId || "",
      });
      getPriceLevelActivities(accountDetails.id);
    }
  }, [accountDetails, reset]);

  // register save handler for this tab
  useEffect(() => {
    setSaveHandler(async () => {
      const values = getValues();
      const priceLevelName = priceLevels.find(
        (level) => level.id === values.priceLevelId
      )?.name;
      const accountName = accountDetails?.name || "this account";

      return createAccountPriceLevel(values, {
        success: {
          text: `Successfully assigned Price Level "${priceLevelName}" to ${accountName}.`,
        },
      });
    });
  }, [priceLevels, accountDetails]);

  const options = priceLevels.map((level) => ({
    label: level.name,
    value: level.id,
  }));

  const groupedData = useMemo(() => {
    return categories.map((category) => {
      const categoryDrop = categoryDrops.filter(
        (drop) => drop.categoryId === category.id
      );
      return {
        category,
        categoryDrop,
      };
    });
  }, [categories, categoryDrops]);

  return (
    <ConfigAccordionHeader title="Price Level" disableCollapse>
      <Box display="flex" flexDirection="column" gap={2}>
        <MiTab
          tabs={internalTabs}
          currentTab={activeTab}
          onTabChange={setActiveTab}
          scrollButtons={false}
          borderBottom
        />
        {activeTab === CustomerPricingDetailsTabs.PriceMatrix && (
          <PriceLevelSelection
            control={control}
            options={options}
            isLoading={isPriceMatrixLoading}
            priceLevels={priceLevels}
            groupedData={groupedData}
          />
        )}
        {activeTab === CustomerPricingDetailsTabs.History && (
          <PriceLevelHistory
            isLoading={isHistoryLoading}
            priceLevelActivities={priceLevelActivities}
          />
        )}
      </Box>
    </ConfigAccordionHeader>
  );
};
